import { MantineProvider } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useEffect, useMemo, useState } from 'react';

import { apiFetch } from '@/api/client';

import { buildTheme, cssVariablesResolver } from '../theme';

import {
  ACCENTS,
  type Appearance,
  BODY_WEIGHT,
  DEFAULT_APPEARANCE,
  headingFontStack,
  inkColors,
  isAppearance,
  uiFontStack,
  withDefaults,
} from './accents';
import { AppearanceContext } from './appearance-context';

const STORAGE_KEY = 'gc_appearance';

interface PublicAppearance extends Appearance {
  clinic_name: string;
}

function readCache(): PublicAppearance | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const value: unknown = raw ? JSON.parse(raw) : null;
    return isAppearance(value)
      ? ({
          ...withDefaults(value),
          clinic_name: (value as PublicAppearance).clinic_name,
        } as PublicAppearance)
      : null;
  } catch {
    return null;
  }
}

function writeCache(value: PublicAppearance) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Stockage indisponible (navigation privée) : l'apparence sera relue du serveur.
  }
}

/** Variables CSS « Gestion Bulletins » qui dépendent des réglages (couleur, police, taille). */
function applyToDocument(a: Appearance) {
  const root = document.documentElement;
  const accent = ACCENTS[a.accent] ?? ACCENTS.vert;
  root.style.setProperty('--accent-light', accent.light);
  root.style.setProperty('--accent-dark', accent.dark);
  root.style.setProperty('--font-ui', uiFontStack(a.font));
  root.style.setProperty('--heading-font', headingFontStack(a));
  root.style.setProperty('--fs', `${a.size}px`);
  // Lisibilité : intensité et épaisseur du texte, fond des pages et des documents.
  const [ink, muted] = inkColors(a);
  root.style.setProperty('--ink-user', ink);
  root.style.setProperty('--muted-user', muted);
  root.style.setProperty('--w-body', String(BODY_WEIGHT[a.text_weight]));
  root.style.setProperty('--w-strong', String(Math.min(800, BODY_WEIGHT[a.text_weight] + 200)));
  root.dataset.pageBg = a.page_background;
  root.dataset.printBg = a.print_background;
}

/**
 * Apparence commune à tous les postes, réglée par l'administrateur (page Apparence).
 * Lue sans connexion pour que l'écran d'accueil ait déjà les bonnes couleurs.
 */
export function AppearanceProvider({
  children,
  remote = true,
}: {
  children: ReactNode;
  /** false : apparence par défaut, sans lecture du serveur (domaine principal). */
  remote?: boolean;
}) {
  const [cached] = useState(readCache);
  const [preview, setPreview] = useState<Appearance | null>(null);
  const query = useQuery({
    queryKey: ['appearance'],
    queryFn: () => apiFetch<PublicAppearance>('/appearance', { noRetry: true }),
    staleTime: 5 * 60_000,
    retry: 1,
    enabled: remote,
  });
  const saved: PublicAppearance = useMemo(
    () =>
      query.data
        ? { ...withDefaults(query.data), clinic_name: query.data.clinic_name }
        : (cached ?? { ...DEFAULT_APPEARANCE, clinic_name: '' }),
    [query.data, cached],
  );

  useEffect(() => {
    if (query.data) writeCache(query.data);
  }, [query.data]);

  const current = preview ?? saved;
  const theme = useMemo(() => buildTheme(current), [current]);
  useEffect(() => applyToDocument(current), [current]);

  const value = useMemo(
    () => ({ appearance: current, saved, clinicName: saved.clinic_name, setPreview }),
    [current, saved],
  );

  return (
    <AppearanceContext.Provider value={value}>
      <MantineProvider
        theme={theme}
        cssVariablesResolver={cssVariablesResolver}
        defaultColorScheme={saved.theme === 'sombre' ? 'dark' : 'light'}
      >
        {children}
      </MantineProvider>
    </AppearanceContext.Provider>
  );
}
